import { Video } from '../hooks/useLectureData';
import { formatDate } from '../util/datetime';

export interface CsvHeader {
  label: string;
  key: string;
}

export interface CsvDataItem {
  id: string;
  email: string;
  lectureName: string;
  lectureStatus: string;
  [key: string]: string | number; // 動的なキーのための型
}

export interface DownloadData {
  id: string;
  email: string;
  lectureName: string;
  lectureStatus: string;
  videos: Video[];
}

export const convertToCSV = (data: DownloadData[]) => {
  let jsonData: CsvDataItem[] = [];
  let maxVideosLength = 0;
  
  data.forEach((item) => {
    if (item.videos.length > maxVideosLength) {
      maxVideosLength = item.videos.length;
    }
  });
  
  let headers: CsvHeader[] = [
    { label: "ユーザーID", key: "id" },
    { label: "メールアドレス", key: "email" },
    { label: "講座名", key: "lectureName" },
    { label: "講座状況", key: "lectureStatus" },
  ];
  
  for (let i = 0; i < maxVideosLength; i++) {
    headers.push({ label: `動画${i + 1}名`, key: `video${i + 1}_name` });
    headers.push({
      label: `動画${i + 1}視聴開始時刻`,
      key: `video${i + 1}_startTime`,
    });
    headers.push({
      label: `動画${i + 1}視聴終了時刻`,
      key: `video${i + 1}_endTime`,
    });
    headers.push({
      label: `動画${i + 1}テスト結果`,
      key: `video${i + 1}_testResult`,
    });
    headers.push({
      label: `動画${i + 1}テスト回答時刻`,
      key: `video${i + 1}_testTime`,
    });
  }
  
  data.forEach((item) => {
    let jsonItem: CsvDataItem = {
      id: item.id,
      email: item.email,
      lectureName: item.lectureName,
      lectureStatus: item.lectureStatus,
    };
    
    item.videos.forEach((video, index) => {
      jsonItem[`video${index + 1}_name`] = video.name || '';
      jsonItem[`video${index + 1}_startTime`] =
        video.startTime !== null ? formatDate(video.startTime || '', true) : "-";
      jsonItem[`video${index + 1}_endTime`] =
        video.endTime !== null ? formatDate(video.endTime || '', true) : "-";
      jsonItem[`video${index + 1}_testResult`] = video.testResult || '';
      jsonItem[`video${index + 1}_testTime`] =
        video.testTime !== null ? formatDate(video.testTime || '', true) : "-";
    });
    
    jsonData.push(jsonItem);
  });
  
  return { jsonData, headers };
};


// ===== CSV インポート(受講者一括登録)用 =====

// バックエンド POST /api/admin/users/bulk に送る 1 件分の入力
export interface BulkUserInput {
  name: string;
  email: string;
  password: string;
}

// パース結果の 1 行(エラー情報付き)
export interface ParsedCsvRow {
  row: number; // CSV データ行番号(1 始まり・ヘッダ除く)
  name: string;
  email: string;
  password: string;
  error?: string; // クライアント側検証エラー(無ければ undefined)
}

export interface CsvParseResult {
  rows: ParsedCsvRow[]; // 全データ行(プレビュー用・エラー情報付き)
  valid: BulkUserInput[]; // 検証を通過した行のみ(送信対象)
  fileError?: string; // ファイル全体のエラー(空・行なし等)
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// CSV の 1 行をダブルクォート対応で分割する(name 等にカンマが含まれるケースに対応)。
const splitCsvLine = (line: string): string[] => {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"'; // "" はエスケープされた "
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields.map((f) => f.trim());
};

// 受講者一括登録 CSV をパース・検証する。
// 期待フォーマット: 各行「名前,メールアドレス,パスワード」。
// 1 行目が name/email/password 等のヘッダなら読み飛ばす。
export const parseBulkUserCsv = (text: string): CsvParseResult => {
  const normalized = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n'); // BOM 除去 + 改行正規化
  const lines = normalized.split('\n').filter((l) => l.trim() !== '');
  if (lines.length === 0) {
    return { rows: [], valid: [], fileError: 'CSV が空です。' };
  }

  // ヘッダ行判定(先頭行に email/name/password/メール 等が含まれていれば読み飛ばす)
  const firstCols = splitCsvLine(lines[0]).map((c) => c.toLowerCase());
  const looksLikeHeader =
    firstCols.includes('email') ||
    firstCols.includes('name') ||
    firstCols.includes('password') ||
    firstCols.some((c) => c.includes('メール') || c.includes('名前') || c.includes('パスワード'));
  const dataLines = looksLikeHeader ? lines.slice(1) : lines;
  if (dataLines.length === 0) {
    return { rows: [], valid: [], fileError: '登録対象の行がありません。' };
  }

  const rows: ParsedCsvRow[] = [];
  const valid: BulkUserInput[] = [];
  const seenEmails = new Set<string>();

  dataLines.forEach((line, index) => {
    const cols = splitCsvLine(line);
    const name = cols[0] ?? '';
    const email = cols[1] ?? '';
    const password = cols[2] ?? '';
    let error: string | undefined;

    if (!name || !email || !password) {
      error = '名前・メールアドレス・パスワードは必須です。';
    } else if (!EMAIL_RE.test(email)) {
      error = 'メールアドレスの形式が不正です。';
    } else if (seenEmails.has(email.toLowerCase())) {
      error = 'CSV 内でメールアドレスが重複しています。';
    }

    if (!error) {
      seenEmails.add(email.toLowerCase());
      valid.push({ name, email, password });
    }
    rows.push({ row: index + 1, name, email, password, error });
  });

  return { rows, valid };
};

// 既存DB重複(サーバ確認結果)をパース結果に反映する。
// existing に含まれる email の行を「既に登録済み」エラー扱いにし、valid(送信対象)から除外する。
// 既にクライアント検証でエラーの行はそのまま(理由を上書きしない)。
export const markExistingEmails = (
  result: CsvParseResult,
  existing: string[]
): CsvParseResult => {
  const set = new Set(existing.map((e) => e.toLowerCase()));
  if (set.size === 0) return result;
  const rows = result.rows.map((r) =>
    !r.error && set.has(r.email.toLowerCase())
      ? { ...r, error: '既に登録済みのメールアドレスです' }
      : r
  );
  const valid = result.valid.filter((v) => !set.has(v.email.toLowerCase()));
  return { ...result, rows, valid };
};